import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../links/API_LINKS';
import {
  Box,
  Card,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Page from '../../../components/Page';
import { useQuery } from 'react-query';
import DataCard from '../../_dashboard/app/DataCard';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TaskIcon from '@mui/icons-material/Task';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DownloadIcon from '@mui/icons-material/Download';
import { useSelector } from 'react-redux';
// @ts-ignore
import { ExportJsonCsv } from 'react-export-json-csv';

const RootStyle = styled(Page)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '& > *': {
    borderBottom: 'unset',
  },
}));

const SubmissionDetails = ({ submission }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackType, setFeedbackType] = useState(''); // 'task2', 'general', 'quiz', or 'solution'
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [selectedSolution, setSelectedSolution] = useState(null);

  const handleViewFeedback = (feedback, type) => {
    setSelectedFeedback(feedback);
    setFeedbackType(type);
    setOpenDialog(true);
  };

  const handleViewQuiz = (taskNo) => {
    const quizData = submission.quiz?.find(q => q.taskNo === taskNo);
    if (quizData) {
      setSelectedQuiz(quizData);
      setFeedbackType('quiz');
      setOpenDialog(true);
    }
  };

  const handleViewSolution = (solution, submitOn) => {
    setSelectedSolution({ content: solution, submitOn });
    setFeedbackType('solution');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFeedback(null);
    setSelectedQuiz(null);
    setSelectedSolution(null);
    setFeedbackType('');
  };

  const renderFeedbackDialog = () => {
    if (!selectedFeedback && !selectedQuiz && !selectedSolution) return null;

    if (feedbackType === 'task2') {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Q1:</strong> {selectedFeedback.q1}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Q2:</strong> {selectedFeedback.q2}
          </Typography>
          {selectedFeedback.q2Explanation && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1 }}>
              {selectedFeedback.q2Explanation}
            </Typography>
          )}
          <Typography variant="subtitle1" gutterBottom>
            <strong>Q3:</strong> {selectedFeedback.q3}
          </Typography>
          {selectedFeedback.q3Explanation && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1 }}>
              {selectedFeedback.q3Explanation}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>Submitted:</strong> {new Date(selectedFeedback.submitTime).toLocaleString()}
          </Typography>
        </Box>
      );
    }

    if (feedbackType === 'quiz') {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Score:</strong> {selectedQuiz.percentage}%
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Start Time:</strong> {new Date(selectedQuiz.startTime).toLocaleString()}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Finish Time:</strong> {new Date(selectedQuiz.finishTime).toLocaleString()}
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            <strong>Responses:</strong>
          </Typography>
          {selectedQuiz.responses?.map((response, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Question {index + 1}:
              </Typography>
              <Typography variant="body1" sx={{ ml: 2 }}>
                {Array.isArray(response) ? response.join(', ') : response}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    if (feedbackType === 'solution') {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Submitted On:</strong> {new Date(selectedSolution.submitOn).toLocaleString()}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <div dangerouslySetInnerHTML={{ __html: selectedSolution.content }} />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        {selectedFeedback.data?.map((item, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Q{index + 1}:</strong> {item.question}
            </Typography>
            <Typography variant="body1" sx={{ ml: 2 }}>
              <strong>Response:</strong> {item.response}
            </Typography>
          </Box>
        ))}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          <strong>Submitted:</strong> {new Date(selectedFeedback.submitTime).toLocaleString()}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ margin: 1 }}>
      <Typography variant="h6" gutterBottom component="div">
        Submission Details
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submission</TableCell>
                <TableCell>Quiz Submissions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submission.tasks?.map((task, index) => (
                <TableRow key={index}>
                  <TableCell>Task {task.taskNo}</TableCell>
                  <TableCell>{task.solution ? 'Completed' : 'Not Submitted'}</TableCell>
                  <TableCell>
                    {task.solution ? (
                      <Button
                        size="small"
                        onClick={() => handleViewSolution(task.solution, task.submitOn)}
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {submission.quiz?.some(q => q.taskNo === task.taskNo) ? (
                      <Button
                        size="small"
                        onClick={() => handleViewQuiz(task.taskNo)}
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                General Feedback
              </Typography>
              {submission.feedback ? (
                <Button
                  size="small"
                  onClick={() => handleViewFeedback(submission.feedback, 'general')}
                  sx={{ textTransform: 'none' }}
                >
                  View Feedback
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No feedback available
                </Typography>
              )}
            </Card>

            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Task 2 Feedback
              </Typography>
              {submission.task2Feedback ? (
                <Button
                  size="small"
                  onClick={() => handleViewFeedback(submission.task2Feedback, 'task2')}
                  sx={{ textTransform: 'none' }}
                >
                  View Feedback
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No feedback available
                </Typography>
              )}
            </Card>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {feedbackType === 'task2' 
            ? 'Task 2 Feedback Details' 
            : feedbackType === 'quiz'
            ? 'Quiz Submission Details'
            : feedbackType === 'solution'
            ? 'Task Solution'
            : 'General Feedback Details'
          }
        </DialogTitle>
        <DialogContent>
          {renderFeedbackDialog()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const UserTable = ({ submissions }) => {
  const [openRows, setOpenRows] = useState({});

  const handleRowClick = (userId) => {
    setOpenRows((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Enrolled Capstones</TableCell>
            <TableCell>Tasks Completed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {submissions?.data?.map((submission) => (
            <React.Fragment key={submission.userId?._id}>
              <StyledTableRow>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleRowClick(submission.userId?._id)}
                  >
                    {openRows[submission.userId?._id] ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell>{`${submission.userId?.firstName || ''} ${submission.userId?.lastName || ''}`}</TableCell>
                <TableCell>{submission.userId?.email}</TableCell>
                <TableCell>{submission.superCapstoneId?.companyId?.name || 'N/A'}</TableCell>
                <TableCell>{submission.tasks?.length || 0}</TableCell>
              </StyledTableRow>
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                  <Collapse in={openRows[submission.userId?._id]} timeout="auto" unmountOnExit>
                    <SubmissionDetails submission={submission} />
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Function to fetch onboarding data for a user
const fetchUserOnboardingData = async (userId) => {
  try {
    const res = await axios.get(`${API_URL}/user/onboard/new-belong-score`, {
      params: { userId }
    });
    return res.data.data;
  } catch (error) {
    console.error(`Error fetching onboarding data for user ${userId}:`, error);
    // Return a default structure so the excel export doesn't fail
    return {
      belongScore: {
        cognitiveAcumen: null,
        skillCompetency: null,
        profileStrength: 0
      },
      profileStrength: 0
    };
  }
};

// Function to fetch submissions
const fetchSubmissions = async (partnerId) => {
  if (!partnerId) return null;

  const res = await axios.get(`${API_URL}/super-capstone/submissions/partner`, {
    params: { 
      partnerId,
    },
  });

  return res.data;
};

const SuperCapstonePartner = () => {
  const [loading, setLoading] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [loadingExcelData, setLoadingExcelData] = useState(false);
  
  // Get current partner from Redux store (partner data is stored here, not user data)
  const currentPartner = useSelector((state) => state.partner.partner);
  
  // Get the partner ID from the nested partner data structure
  const userPartnerId = currentPartner?.data?.partnerId?._id;
  // console.log('User Partner ID:', userPartnerId);

  // Console log partner data and partnerId
  // React.useEffect(() => {
  //   console.log('Current Partner Data:', currentPartner);
  //   console.log('Extracted Partner ID:', userPartnerId);
  //   console.log('Full Partner Object:', currentPartner?.data?.partnerId);
  // }, [currentPartner, userPartnerId]);

  // Fetch submissions for the user's associated partner
  const {
    data: submissions,
    status: submissionsStatus,
    error: submissionsError,
  } = useQuery(
    ['fetchSubmissions', userPartnerId],
    () => {
      // console.log('Fetching submissions for Partner ID:', userPartnerId);
      // console.log('submissions',submissions);

      return fetchSubmissions(userPartnerId);
    },
    {
      enabled: !!userPartnerId,
      onSettled: () => setLoading(false),
      retry: 3,
      refetchOnWindowFocus: false,
    }
  );

  // Effect to prepare Excel data when submissions change
  useEffect(() => {
    const prepareExcelData = async () => {
      if (!submissions?.data) {
        setExcelData([]);
        return;
      }

      setLoadingExcelData(true);
      
      try {
        const data = await Promise.all(
          submissions.data.map(async (submission) => {
            // Get completed task numbers for quiz submission column
            const completedTaskNumbers = submission.tasks?.map(task => `Task ${task.taskNo}`).join(', ') || '';
            
            // Fetch onboarding data for the user
            const onboardingData = await fetchUserOnboardingData(submission.userId?._id);
            
            // Check if user has given aptitude quiz (check if cognitiveAcumen exists and has a value)
            const aptitudeGiven = onboardingData?.belongScore?.cognitiveAcumen ? 'Yes' : 'No';
            
            // Check if user has given domain quiz (check if skillCompetency exists and has a value)
            const domainQuizGiven = onboardingData?.belongScore?.skillCompetency ? 'Yes' : 'No';
            
            // Get profile strength value from belongScore first, then fallback to top-level
            const profileStrength = onboardingData?.belongScore?.profileStrength ?? onboardingData?.profileStrength ?? 0;
            const formattedProfileStrength = Number(profileStrength).toFixed(2);
            
            return {
              'Student Name': `${submission.userId?.firstName || ''} ${submission.userId?.lastName || ''}`.trim(),
              'Email': submission.userId?.email || '',
              'Company': submission.superCapstoneId?.companyId?.name || 'N/A',
              'Enrolled Capstone': submission.superCapstoneId?.title || 'N/A',
              'Enrollment Date/time': submission.createdAt ? new Date(submission.createdAt).toLocaleString() : 'N/A',
              'Total Tasks Completed': submission.tasks?.length || 0,
              'Certificate Unlocked': submission.feedback ? 'Yes' : 'No',
              'Quiz Submitted': completedTaskNumbers || 'None',
              'Quiz Score': submission.quiz?.map(q => `Task ${q.taskNo}: ${q.percentage}%`).join(', ') || '-',
              'Aptitude Given': aptitudeGiven,
              'Domain Quiz Given': domainQuizGiven,
              'Profile Strength': formattedProfileStrength,
            };
          })
        );
        
        setExcelData(data);
      } catch (error) {
        console.error('Error preparing Excel data:', error);
        setExcelData([]);
      } finally {
        setLoadingExcelData(false);
      }
    };

    prepareExcelData();
  }, [submissions]);

  // Calculate total enrolled students
  const totalEnrolledStudents = submissions?.data?.length || 0;

  // Calculate task completion statistics
  const taskCompletionStats = React.useMemo(() => {
    if (!submissions?.data) return {
      completed1Task: 0,
      completed2Tasks: 0,
      completed3Tasks: 0,
      completed4Tasks: 0
    };
    
    const stats = submissions.data.reduce((acc, submission) => {
      const completedTasks = submission.tasks?.length || 0;
      
      if (completedTasks >= 1) acc.completed1Task++;
      if (completedTasks >= 2) acc.completed2Tasks++;
      if (completedTasks >= 3) acc.completed3Tasks++;
      if (completedTasks >= 4) acc.completed4Tasks++;
      return acc;
    }, {
      completed1Task: 0,
      completed2Tasks: 0,
      completed3Tasks: 0,
      completed4Tasks: 0
    });

    return stats;
  }, [submissions]);

  const excelHeaders = [
    { key: 'Student Name', name: 'Student Name' },
    { key: 'Email', name: 'Email' },
    { key: 'Company', name: 'Company' },
    { key: 'Enrolled Capstone', name: 'Enrolled Capstone' },
    { key: 'Enrollment Date/time', name: 'Enrollment Date/time' },
    { key: 'Total Tasks Completed', name: 'Total Tasks Completed' },
    { key: 'Certificate Unlocked', name: 'Certificate Unlocked' },
    { key: 'Quiz Submitted', name: 'Quiz Submitted' },
    { key: 'Quiz Score', name: 'Quiz Score (%)' },
    { key: 'Aptitude Given', name: 'Aptitude Given' },
    { key: 'Domain Quiz Given', name: 'Domain Quiz Given' },
    { key: 'Profile Strength', name: 'Profile Strength' },
  ];

  return (
    <RootStyle title='Super Capstone Partner Submissions'>
      <Container>
        <Box sx={{ mb: 5 }}>
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center"
            spacing={2}
          >
            <Typography variant='h4'>
              Super Capstone Partner Submissions
            </Typography>
            {submissions && submissions.data && submissions.data.length > 0 && (
              <ExportJsonCsv 
                headers={excelHeaders} 
                items={excelData}
                filename="super-capstone-submissions"
              >
                <Button 
                  variant="contained" 
                  startIcon={loadingExcelData ? <CircularProgress size={20} /> : <DownloadIcon />}
                  sx={{ minWidth: 'auto' }}
                  disabled={loadingExcelData || excelData.length === 0}
                >
                  {loadingExcelData 
                    ? 'Preparing Data...' 
                    : excelData.length === 0 
                      ? 'No Data Ready' 
                      : `Download Excel Report (${excelData.length} rows)`
                  }
                </Button>
              </ExportJsonCsv>
            )}
          </Stack>
        </Box>

        <Card sx={{ p: 3 }}>
          {(loading || submissionsStatus === 'loading') && (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              py={3}>
              <CircularProgress />
            </Box>
          )}

          {submissionsStatus === 'error' && (
            <Typography color='error' sx={{ mt: 2 }}>
              Error: {submissionsError?.message || 'Failed to load submissions'}
            </Typography>
          )}

          {!userPartnerId && (
            <Typography color='text.secondary' sx={{ mt: 2 }}>
              No partner data available for your account.
            </Typography>
          )}

          {submissions && submissionsStatus === 'success' && (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <DataCard
                    total={totalEnrolledStudents}
                    title='Total Enrolled Students'
                    icon={PeopleIcon}
                    bgColor='#C8FACD'
                    textColor='#005249'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DataCard
                    total={taskCompletionStats.completed1Task}
                    title='Completed 1 Task'
                    icon={AssignmentIcon}
                    bgColor='#D0F2FF'
                    textColor='#04297A'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DataCard
                    total={taskCompletionStats.completed2Tasks}
                    title='Completed 2 Tasks'
                    icon={TaskIcon}
                    bgColor='#FFF7CD'
                    textColor='#7A4F01'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DataCard
                    total={taskCompletionStats.completed3Tasks}
                    title='Completed 3 Tasks'
                    icon={CheckCircleIcon}
                    bgColor='#FFE7D9'
                    textColor='#7A0C2E'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DataCard
                    total={taskCompletionStats.completed4Tasks}
                    title='Completed 4 Tasks'
                    icon={EmojiEventsIcon}
                    bgColor='#E9FCD4'
                    textColor='#08660D'
                  />
                </Grid>
              </Grid>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Student Submissions
              </Typography>
              <UserTable submissions={submissions} />
            </>
          )}
        </Card>
      </Container>
    </RootStyle>
  );
};

export default SuperCapstonePartner;
